import { useMemo } from "react";
import { CloseTemplateViewConfig } from "@common/types/closeTemplateViewConfig";
import { TaskType } from "@common/types/task";
import { InvoiceViewSchema } from "@common/types/views/invoiceViews";
import { isInvoiceProviderAccount } from "@helpers/invoice";
import { createInvoicesViewConfig } from "@components/invoices/views/invoicesView/invoicesViewConfig";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import {
    ORG_LEVEL_INVOICES_FILTER_TABS,
    WORKSPACE_INVOICES_FILTER_TABS,
} from "@components/tasks/TaskBody/Invoices/invoicesTaskFilters";
import {
    defaultInvoicesView,
    parseInvoicesTaskView,
    serializeInvoicesTemplateView,
} from "@components/tasks/TaskBody/Invoices/invoicesTaskView";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { useScope } from "@state/scopeContext.lib";
import styles from "./CloseTemplateTaskTransactionsPreview.module.scss";
import { TemplateInvoicesDataTable } from "./dataPreview/TemplateInvoicesDataTable";
import { TemplatePreviewSwitcher } from "./dataPreview/TemplatePreviewSwitcher";
import { useTemplateTaskPreview } from "./dataPreview/useTemplateTaskPreview";
import { useCloseTemplateTaskViewState } from "./useCloseTemplateTaskViewState";

interface Props {
    viewConfig: CloseTemplateViewConfig | null;
    onViewConfigChange: (viewConfig: CloseTemplateViewConfig) => void;
}

/**
 * Right-panel content for the Add/Edit Close Template Task Drawer when the
 * task type is INVOICES.
 *
 * Shows a read-only invoices table with the rows the task would show for the
 * previewed client and entity (see `dataPreview/`), with column management and
 * filters. Without a workspace (org-level, non client specific template) only
 * workspace-agnostic filters are offered; a workspace-scoped template offers
 * every invoice filter. The entity is not configurable here: apply injects the
 * entity receiving the task.
 */
export const CloseTemplateTaskInvoicesPreview: React.FC<Props> = ({
    viewConfig,
    onViewConfigChange,
}) => {
    const { workspaceId } = useScope();
    const { view, handleViewChange, handleFiltersChange } =
        useCloseTemplateTaskViewState({
            viewConfig,
            onViewConfigChange,
            parseViewConfig: parseInvoicesTaskView,
            serializeView: serializeInvoicesTemplateView,
            viewSchema: InvoiceViewSchema,
        });

    const tableViewConfig = useMemo(() => createInvoicesViewConfig(), []);
    const defaultView = useMemo(() => defaultInvoicesView(), []);
    const availableFilters = workspaceId
        ? WORKSPACE_INVOICES_FILTER_TABS
        : ORG_LEVEL_INVOICES_FILTER_TABS;

    const { previewTarget, resolvedView } = useTemplateTaskPreview({
        taskType: TaskType.INVOICES,
        viewConfig: viewConfig ?? serializeInvoicesTemplateView(view),
    });

    return (
        <div>
            <TablePageHeader
                className={styles.filters}
                filters={view.filters}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                accountFilterPredicate={isInvoiceProviderAccount}
                counterpartyEntityLabel="Customer"
                counterpartyInvoicesOnly
                shouldShowKickAccountOption
                isFullPage
            >
                <ColumnSettings
                    view={view}
                    viewConfig={tableViewConfig}
                    defaultView={defaultView}
                    onViewChange={handleViewChange}
                />
                <TemplatePreviewSwitcher target={previewTarget} />
            </TablePageHeader>

            <TemplateInvoicesDataTable
                templateView={view}
                onTemplateViewChange={handleViewChange}
                resolvedView={resolvedView}
                workspaceId={previewTarget.workspaceId}
            />
        </div>
    );
};
